/**
 * Sent → delivered → opened → clicked, with the drop between each pair.
 *
 * A funnel rather than four tiles because the useful quantity is the *loss*, and
 * four independent numbers make the reader compute it. "Delivered 4,050" and
 * "Opens 2,266" are two facts; "44% of delivered mail was never opened" is the
 * one somebody acts on.
 *
 * THREE DECISIONS WORTH KEEPING.
 *
 * Stages count messages, not events. A funnel says each stage is part of the
 * one above it, and events break that: a reader who opens twice and clicks two
 * links is two opens and two clicks, so opens could exceed deliveries. The
 * event totals stay beside the stages, because they say how much activity
 * there was; they are just not a stage. A click counts as an open, so the
 * click stage can never outgrow the one above it.
 *
 * Each stage divides by the one above it, not by `sent`. Opens as a share of
 * sent understates every campaign in proportion to how badly it bounced, which
 * is exactly backwards — a message that never arrived is not an engagement
 * failure. The share of the original send is shown too, in smaller type, because
 * it is the number people quote.
 *
 * The open stage is drawn twice. Apple Mail Privacy Protection pre-fetches
 * images, so a single open figure is either inflated or misreported and there is
 * no third option that is honest. The MPP-excluded count is a second bar inside
 * the first, and the gap between them is the inflation — visible rather than
 * explained in a footnote nobody reads.
 */

import { formatRate, rate, type Totals } from "./rates";

interface FunnelProps {
  totals: Totals;
}

interface Stage {
  label: string;
  value: number;
  /** The stage above, which this is a share of. */
  of: number;
  ofLabel: string;
  /** Drawn as a lighter bar inside this one. */
  inner?: { value: number; label: string };
  status: "sent" | "delivered" | "opened" | "clicked";
  note?: string;
  /** The event total behind a stage that counts messages. */
  events?: string;
}

export function Funnel({ totals }: FunnelProps) {
  const stages: Stage[] = [
    {
      label: "Sent",
      value: totals.sent,
      of: totals.sent,
      ofLabel: "of sent",
      status: "sent",
    },
    {
      label: "Delivered",
      value: totals.delivered,
      of: totals.sent,
      ofLabel: "of sent",
      status: "delivered",
      note: lostNote(totals.sent - totals.delivered, "never arrived"),
    },
    {
      label: "Opened",
      value: totals.unique.opened,
      of: totals.delivered,
      ofLabel: "of delivered",
      status: "opened",
      inner: { value: totals.unique.opened_excluding_mpp, label: "excluding MPP" },
      note: lostNote(totals.delivered - totals.unique.opened, "were not opened"),
      events: eventsNote(totals.opens.total, "open", "opens"),
    },
    {
      label: "Clicked",
      value: totals.unique.clicked,
      of: totals.unique.opened,
      ofLabel: "of opened",
      status: "clicked",
      events: eventsNote(totals.clicked, "click", "clicks"),
    },
  ];

  // Every bar is a share of the widest stage, so the shape reads as a funnel.
  // Scaling each to its own denominator would draw four full-width bars.
  const widest = Math.max(totals.sent, 1);

  return (
    <div className="funnel">
      {stages.map((stage) => {
        const share = rate(stage.value, stage.of);
        const width = (stage.value / widest) * 100;
        const innerWidth = stage.inner ? (stage.inner.value / widest) * 100 : 0;

        return (
          <div className="funnel__stage" key={stage.label}>
            <div className="funnel__head">
              <span className="oc-label">{stage.label}</span>
              <span className="funnel__value">{stage.value.toLocaleString()}</span>
              <span className="oc-body-sm oc-text-secondary funnel__share">
                {formatRate(share)} {stage.ofLabel}
                {stage.events && <> · {stage.events}</>}
              </span>
            </div>

            <div className="funnel__track">
              <div
                className={`funnel__bar funnel__bar--${stage.status}`}
                style={{ width: `${width}%` }}
              />
              {stage.inner && (
                <div
                  className="funnel__bar funnel__bar--inner"
                  style={{ width: `${innerWidth}%` }}
                  title={`${stage.inner.value.toLocaleString()} ${stage.inner.label}`}
                />
              )}
            </div>

            {stage.inner && totals.opens.mpp > 0 && (
              <p className="oc-body-sm oc-text-secondary funnel__note">
                {stage.inner.value.toLocaleString()} {stage.inner.label} —{" "}
                {formatRate(rate(totals.opens.mpp, totals.opens.total))} of opens are
                Apple pre-fetching images rather than somebody reading. That share is a
                floor: attribution is by user agent, so an MPP open that does not
                identify itself is counted as an ordinary one.
              </p>
            )}

            {stage.note && (
              <p className="oc-body-sm oc-text-secondary funnel__note">{stage.note}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function lostNote(lost: number, what: string): string | undefined {
  if (lost <= 0) return undefined;
  return `${lost.toLocaleString()} ${what}.`;
}

/** "1,204 opens in all": the events behind a stage that counts messages. */
function eventsNote(count: number, one: string, many: string): string | undefined {
  if (count <= 0) return undefined;
  return `${count.toLocaleString()} ${count === 1 ? one : many} in all`;
}
